import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Heart, X, Clock, BookOpen, PenLine, Feather } from "lucide-react";

const ProfileCard = ({ s, onPass, idx }) => {
  const nav = useNavigate();
  return (
    <article
      data-testid={`human-card-${s.story_id}`}
      style={{ animationDelay: `${idx * 70}ms` }}
      className="group relative rounded-[28px] overflow-hidden border border-white/10 bg-[#0F1117] shadow-[0_30px_80px_-40px_rgba(244,63,94,0.45)] animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        {s.cover_url ? (
          <img src={fileUrl(s.cover_url)} alt={s.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-rose-900/60 via-[#1a1020] to-amber-900/40" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080A] via-[#07080A]/40 to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="rounded-full bg-black/50 backdrop-blur-md border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-rose-200 flex items-center gap-1.5"><Feather className="h-3 w-3" /> Human-written</span>
        </div>
        <div className="absolute bottom-0 inset-x-0 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-11 w-11 rounded-full ring-2 ring-rose-400/70 overflow-hidden bg-rose-500/20 flex items-center justify-center text-rose-200 font-semibold">
              {s.author_picture ? <img src={s.author_picture} alt="" className="h-full w-full object-cover" /> : (s.author_name?.[0] || "A")}
            </div>
            <div className="min-w-0">
              <div data-testid={`human-card-author-${s.story_id}`} className="text-sm text-white font-medium truncate">{s.author_name}</div>
              <div className="text-[11px] text-slate-400 truncate">{s.about_author || "Storyteller on ProSeCraft"}</div>
            </div>
          </div>
          <h3 className="font-serif text-2xl font-bold leading-tight text-white">{s.title}</h3>
          <p className="text-sm text-slate-300 mt-1 line-clamp-2">{s.tagline}</p>
        </div>
      </div>
      <div className="px-5 pb-5 pt-1">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(s.genres || [s.genre]).filter(Boolean).map((g) => (
            <span key={g} className="rounded-full border border-amber-400/30 text-amber-200 px-2.5 py-0.5 text-[11px]">{g}</span>
          ))}
          <span className="rounded-full border border-white/10 text-slate-400 px-2.5 py-0.5 text-[11px] flex items-center gap-1"><Clock className="h-3 w-3" /> {s.read_minutes} min</span>
          <span className="rounded-full border border-white/10 text-slate-400 px-2.5 py-0.5 text-[11px] flex items-center gap-1"><BookOpen className="h-3 w-3" /> {s.chapters_count} ch</span>
        </div>
        {s.excerpt && <p className="font-serif italic text-[13px] text-slate-400 line-clamp-3 border-l-2 border-rose-400/40 pl-3">“{s.excerpt}…”</p>}
        <div className="mt-5 flex items-center justify-center gap-5">
          <button data-testid={`human-pass-${s.story_id}`} onClick={() => onPass(s.story_id)} aria-label="Pass" className="h-14 w-14 rounded-full border border-white/15 text-slate-300 flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:border-slate-300 active:scale-95">
            <X className="h-6 w-6" />
          </button>
          <button data-testid={`human-read-${s.story_id}`} onClick={() => nav(`/story/${s.story_id}`)} aria-label="Read" className="h-16 w-16 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 text-black flex items-center justify-center shadow-lg shadow-rose-500/30 transition-transform duration-200 hover:scale-110 active:scale-95">
            <Heart className="h-7 w-7" fill="currentColor" />
          </button>
        </div>
      </div>
    </article>
  );
};

export default function HumanStories() {
  const [rows, setRows] = useState(null);
  const [genre, setGenre] = useState("All");
  const [passed, setPassed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("human_passed") || "[]"); } catch { return []; }
  });

  useEffect(() => { api.get("/stories/human").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const pass = (id) => {
    const next = [...passed, id];
    setPassed(next);
    try { localStorage.setItem("human_passed", JSON.stringify(next)); } catch { /* ignore */ }
  };
  const genres = useMemo(() => ["All", ...new Set((rows || []).flatMap((s) => s.genres || [s.genre]).filter(Boolean))], [rows]);
  const visible = (rows || []).filter((s) => !passed.includes(s.story_id) && (genre === "All" || (s.genres || [s.genre]).includes(genre)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="human-stories-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-rose-400 mb-2 flex items-center gap-2"><Heart className="h-3 w-3" /> Human Hearts</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Meet your next story.</h1>
          <p className="mt-3 text-slate-400 max-w-xl">Hand-written by real readers, reviewed by our editors. Tap the heart to read — or pass and meet someone new.</p>
        </div>
        <Link to="/write" data-testid="human-write-cta" className="inline-flex items-center gap-2 rounded-full bg-rose-500 hover:bg-rose-400 text-black font-semibold px-6 h-12 self-start md:self-auto transition-colors">
          <PenLine className="h-4 w-4" /> Share your story
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {genres.map((g) => (
          <button key={g} data-testid={`human-genre-${g}`} onClick={() => setGenre(g)} className={`rounded-full px-4 h-9 text-xs font-mono uppercase tracking-widest border transition-colors ${genre === g ? "bg-rose-500 text-black border-rose-500" : "border-white/10 text-slate-400 hover:text-rose-200"}`}>{g}</button>
        ))}
        {passed.length > 0 && (
          <button data-testid="human-reset-passed" onClick={() => { setPassed([]); localStorage.removeItem("human_passed"); }} className="rounded-full px-4 h-9 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-amber-300">Show passed ({passed.length})</button>
        )}
      </div>

      {rows === null ? (
        <div className="text-slate-500">Loading stories…</div>
      ) : visible.length === 0 ? (
        <div data-testid="human-empty" className="rounded-3xl border border-dashed border-white/10 p-12 text-center text-slate-400">
          {rows.length > 0 ? "You've seen everyone here — tap “Show passed” or try another genre." : <>No stories here yet. <Link to="/write" className="text-rose-300 underline">Be the first to share one</Link>.</>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((s, i) => <ProfileCard key={s.story_id} s={s} idx={i} onPass={pass} />)}
        </div>
      )}
    </div>
  );
}
